import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "app_mode_provider_slug";

export interface AppModeProvider {
  id: string;
  name: string;
  slug: string;
  image_url: string | null;
  display_name: string | null;
  brand_primary_color: string | null;
  brand_secondary_color: string | null;
  brand_background_color: string | null;
  brand_heading_color: string | null;
  brand_body_color: string | null;
  brand_header_text_color: string | null;
  brand_font: string | null;
  email: string | null;
  phone: string | null;
  provider_contact_email: string | null;
}

/**
 * Detect provider-locked app mode from (in priority order):
 *   1. URL ?app=<slug>
 *   2. localStorage
 *   3. VITE_APP_MODE_PROVIDER (Capacitor build)
 */
export const useAppMode = () => {
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get("app");
  const envSlug =
    (import.meta.env.VITE_APP_MODE_PROVIDER as string | undefined) || null;

  const [storedSlug, setStoredSlug] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(STORAGE_KEY);
  });

  // Persist the slug from query param
  useEffect(() => {
    if (queryParam) {
      localStorage.setItem(STORAGE_KEY, queryParam);
      setStoredSlug(queryParam);
    }
  }, [queryParam]);

  const slug = queryParam || storedSlug || envSlug;

  const { data: provider, isLoading } = useQuery({
    queryKey: ["app-mode-provider", slug],
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("providers")
        .select(
          "id, name, slug, image_url, display_name, brand_primary_color, brand_secondary_color, brand_background_color, brand_heading_color, brand_body_color, brand_header_text_color, brand_font, email, phone, provider_contact_email"
        )
        .eq("slug", slug)
        .maybeSingle();

      if (error) {
        console.error("useAppMode: error fetching provider", error);
        return null;
      }
      return data as AppModeProvider | null;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 10,
  });

  const clearAppMode = () => {
    localStorage.removeItem(STORAGE_KEY);
    setStoredSlug(null);
  };

  return {
    isAppMode: !!slug && !!provider,
    slug,
    provider: provider || null,
    isLoading: !!slug && isLoading,
    clearAppMode,
  };
};
